import { useMemo } from "react";

import type { EnergyAnalysisDimension, EnergyAnalysisRow, EnergyAnalysisViewModel } from "../model/types";

import { buildCategoryTotals, buildTrendSeries, buildYearlyTableRows, collectYears } from "../lib/aggregate";
import { buildMixCategoryTotals, buildMixCells, buildMixTableRows, collectMixAxes } from "../lib/aggregateMix";
import { ENERGY_ANALYSIS_DIMENSION, ENERGY_ANALYSIS_TOP_KPI_COUNT } from "../model/constants";

const resolveReferenceYear = (rows: EnergyAnalysisRow[], lastReportedYear?: number) => {
  if (lastReportedYear !== undefined && rows.some((row) => row.year === lastReportedYear)) return lastReportedYear;
  const years = collectYears(rows);
  return years[years.length - 1];
};

export const useEnergyAnalysisViewModel = (
  rows: EnergyAnalysisRow[],
  dimension: EnergyAnalysisDimension,
  unit: string,
  referenceYearHint?: number
): EnergyAnalysisViewModel => {
  return useMemo(() => {
    const years = collectYears(rows);
    const referenceYear = resolveReferenceYear(rows, referenceYearHint);
    const isMix = dimension === ENERGY_ANALYSIS_DIMENSION.mix;
    if (isMix) {
      const mixRows = referenceYear === undefined ? rows : rows.filter((row) => row.year === referenceYear);
      const { useTypes, utilities } = collectMixAxes(rows);
      const stableMixCells = buildMixCells(rows);
      const selectedCells = new Map(
        buildMixCells(mixRows).map((cell) => [`${cell.useType}::${cell.utility}`, cell.value])
      );
      const mixCells = stableMixCells.map((cell) => ({
        ...cell,
        value: selectedCells.get(`${cell.useType}::${cell.utility}`) ?? 0,
      }));
      const totalValue = mixCells.reduce((total, cell) => total + cell.value, 0);
      const categoryTotals = buildMixCategoryTotals(mixCells, totalValue);
      const snapshotValueMax = Math.max(
        ...years.flatMap((year) =>
          buildMixCells(rows.filter((row) => row.year === year)).map((cell) => cell.value)
        ),
        0
      );
      return {
        categoryTotals,
        dimension,
        mixCells,
        mixUseTypes: useTypes,
        mixUtilities: utilities,
        referenceYear,
        snapshotValueMax,
        tableCategoryKeys: utilities,
        tableRows: buildMixTableRows(mixCells, useTypes, utilities),
        topCategories: categoryTotals.slice(0, ENERGY_ANALYSIS_TOP_KPI_COUNT),
        totalValue,
        trendSeries: [],
        unit,
        years,
      };
    }
    const splitDimension = dimension === ENERGY_ANALYSIS_DIMENSION.utility ? "utility" : "useType";
    const { series } = buildTrendSeries(rows, splitDimension);
    const referenceRows = referenceYear === undefined ? rows : rows.filter((row) => row.year === referenceYear);
    const allYearTotals = buildCategoryTotals(rows, splitDimension);
    const referenceTotals = buildCategoryTotals(referenceRows, splitDimension);
    const referenceTotal = referenceTotals.reduce((total, item) => total + item.value, 0);
    const referenceByKey = new Map(referenceTotals.map((item) => [item.key, item]));
    const categoryTotals = allYearTotals.map((stableItem) => {
      const referenceItem = referenceByKey.get(stableItem.key);
      const value = referenceItem?.value ?? 0;
      return {
        ...stableItem,
        share: referenceTotal === 0 ? 0 : value / referenceTotal,
        value,
      };
    });
    const snapshotValueMax = Math.max(...series.flatMap((item) => item.data), 0);
    return {
      categoryTotals,
      dimension,
      mixCells: [],
      mixUseTypes: [],
      mixUtilities: [],
      referenceYear,
      snapshotValueMax,
      tableCategoryKeys: allYearTotals.map((item) => item.key),
      tableRows: buildYearlyTableRows(rows, splitDimension),
      topCategories: categoryTotals.filter((item) => item.value > 0).slice(0, ENERGY_ANALYSIS_TOP_KPI_COUNT),
      totalValue: categoryTotals.reduce((total, item) => total + item.value, 0),
      trendSeries: series,
      unit,
      years,
    };
  }, [dimension, referenceYearHint, rows, unit]);
};
